import React, { memo, useMemo } from "react"

import {
  yesNoBadgeColorClasses,
  yesNoBadgeEmptyClasses,
  yesNoBadgeShapeClasses,
} from "@/metrics/metric-display"
import { useUser } from "@clerk/nextjs"

import type { Annotation } from "@/types/api/Annotation"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import {
  COMPOSITE_AI_COLUMN_CLASS,
  COMPOSITE_HUMAN_COLUMN_CLASS,
  getCompositeResultData,
} from "./composite-evaluation-types"
import { getCompositeJudgeScore } from "./composite-evaluation-values"

interface CompositeJudgeScoresProps {
  annotationsForMetric: Annotation[]
  metricOutput?: RunMetricOutput
  metadataCriteria?: string[] | null
  reportingMethod: string
  /** Blind review hides the machine total so the reviewer isn't anchored. */
  showAiScore?: boolean
}

/** A judge's total, or a placeholder while that judge has yet to rule on every
 *  criterion, so the chip keeps its footprint either way. The column beneath
 *  names the judge, so the chip carries no label of its own. */
const JudgeScore: React.FC<{
  score: { label: string; passed: boolean | null } | null
  judge: string
}> = ({ score, judge }) => {
  const color =
    score == null
      ? yesNoBadgeEmptyClasses
      : score.passed == null
        ? yesNoBadgeColorClasses.default
        : score.passed
          ? yesNoBadgeColorClasses.success
          : yesNoBadgeColorClasses.danger
  return (
    <span
      className={`${yesNoBadgeShapeClasses} ${color}`}
      aria-label={
        score == null ? `No ${judge} score yet` : `${judge}: ${score.label}`
      }
    >
      {score?.label ?? "-"}
    </span>
  )
}

/**
 * Both judges' totals for one composite metric, in the card's top row above the
 * criteria table's own AI and Human columns. They are computed the same way —
 * the AI's from its per-criterion verdicts, the reviewer's from their votes —
 * so the two read as comparable.
 */
export const CompositeJudgeScores: React.FC<CompositeJudgeScoresProps> = memo(
  ({
    annotationsForMetric,
    metricOutput,
    metadataCriteria,
    reportingMethod,
    showAiScore = true,
  }) => {
    const { user } = useUser()
    const userEmail = user?.emailAddresses?.[0]?.emailAddress

    const myAnnotation = useMemo(
      () => annotationsForMetric.find((a) => a.assignee === userEmail) ?? null,
      [annotationsForMetric, userEmail],
    )

    const resultJson = getCompositeResultData(metricOutput)
    const aiValues = resultJson?.individual_results?.map(
      (result) => result.result,
    )
    const criteriaCount = aiValues?.length ?? metadataCriteria?.length ?? 0

    if (criteriaCount === 0) {
      return null
    }

    return (
      // Right-anchored on the same widths as the table's columns, which is
      // what puts each chip above the column it summarizes.
      <div className="flex w-full justify-end">
        {showAiScore && (
          <div className={`${COMPOSITE_AI_COLUMN_CLASS} px-3`}>
            <JudgeScore
              judge="AI"
              score={getCompositeJudgeScore(
                aiValues,
                reportingMethod,
                criteriaCount,
              )}
            />
          </div>
        )}
        <div className={`${COMPOSITE_HUMAN_COLUMN_CLASS} px-3`}>
          <JudgeScore
            judge="Human"
            score={getCompositeJudgeScore(
              myAnnotation?.ground_truth_set_value,
              reportingMethod,
              criteriaCount,
            )}
          />
        </div>
      </div>
    )
  },
)
CompositeJudgeScores.displayName = "CompositeJudgeScores"
